// src/utils/currencyUtil.js
//
// Every money figure in the app is printed through here, so a club that is
// not billing in pesos changes one config field instead of the ~30 files that
// used to carry a hardcoded sign.

import { DEFAULT_CURRENCY_SYMBOL } from '../constants/bookingConst';

/**
 * The symbol to print in front of an amount, from Config -> System.
 *
 * Falls back to the peso rather than to an empty string: a blank field would
 * otherwise render bare numbers with no indication of currency at all, which
 * is worse than showing the wrong symbol, and every club that existed before
 * this setting did was billing in pesos.
 *
 * @param {object} [config] - the club config (ConfigContext).
 * @returns {string}
 */
export const getCurrencySymbol = (config) => {
  const configured = (config?.currencySymbol || '').trim();
  return configured || DEFAULT_CURRENCY_SYMBOL;
};

/**
 * The club's own name for its currency ("Philippine Peso"), from
 * Config -> System. Purely descriptive — it labels the setting for whoever
 * edits it later and is not printed next to amounts.
 *
 * @param {object} [config]
 * @returns {string} '' when unset, so callers can skip rendering it.
 */
export const getCurrencyDescription = (config) => (config?.currencyDescription || '').trim();

/**
 * Formats an amount the way every screen should show it: grouped thousands,
 * with the configured symbol in front.
 *
 * DECIMALS ARE SHOWN ONLY WHEN THE AMOUNT HAS THEM. A club charging 300 still
 * sees "300", not "300.00" — this was added to SUPPORT other currencies, not
 * to restyle the peso deployments that already exist. An amount of 300.5
 * prints "300.50", padded to two places so it reads as money rather than as a
 * truncated number.
 *
 * Rounds to two decimal places first, so a value that picked up floating-point
 * dust in arithmetic (0.1 + 0.2) prints as money instead of as 0.30000000000000004.
 *
 * A non-numeric or missing amount formats as zero rather than "NaN" —
 * a price that has not loaded yet should read as free-of-charge-so-far, not
 * as a broken screen.
 *
 * @param {number|string} value - the amount.
 * @param {object} [config] - the club config, for the symbol.
 * @returns {string} e.g. "₱1,250", "$19.99"
 */
export const formatMoney = (value, config) => `${getCurrencySymbol(config)}${formatAmount(value)}`;

/**
 * The number half of formatMoney, with no symbol — for the few places that
 * print the symbol separately (a column header that already says "₱", a
 * spreadsheet cell that should stay numeric-looking).
 *
 * @param {number|string} value
 * @returns {string}
 */
export const formatAmount = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '0';

  // Round before testing for a fractional part: 299.999999 is a whole 300 as
  // far as money is concerned, and should print as one.
  const rounded = Math.round(amount * 100) / 100;
  const fractionDigits = Number.isInteger(rounded) ? 0 : 2;

  return rounded.toLocaleString(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: 2,
  });
};

/**
 * Rounds an amount to the smallest unit money is actually handled in.
 *
 * ARITHMETIC, not display — formatAmount rounds on the way to the screen, but
 * a figure that is stored, or added to another figure, has to be rounded
 * before it is used or the dust compounds: a VAT line printed as ₱16.80 while
 * ₱16.799999999999997 is what gets stored and subtracted leaves a breakdown
 * whose parts do not add up to its own total.
 *
 * A non-numeric amount rounds to 0, matching formatAmount rather than
 * returning NaN into a sum.
 *
 * @param {number|string} value
 * @returns {number} the amount to two decimal places
 */
export const roundMoney = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return 0;
  return Math.round(amount * 100) / 100;
};

/**
 * Reads a price typed into an admin field. Unlike parseInt — which is what
 * every rate input used before currencies with sub-unit pricing were
 * supported — this keeps the decimal part, and rounds to two places so a
 * pasted 19.999 cannot be stored as a price no one can actually pay.
 *
 * @param {string|number} value - the raw input value.
 * @param {number} [fallback=0] - returned for a blank or unparseable field.
 * @returns {number}
 */
export const parsePriceInput = (value, fallback = 0) => {
  if (value === '' || value === null || value === undefined) return fallback;
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.round(parsed * 100) / 100;
};
